import { useContext, useEffect, useMemo, useState } from 'react';
import {
  GridLayout,
  noCompactor,
  useContainerWidth,
  type LayoutItem,
} from 'react-grid-layout';
import { GridBackground } from 'react-grid-layout/extras';
import 'react-grid-layout/css/styles.css';
import 'react-resizable/css/styles.css';
import {
  Button,
  Card,
  Skeleton,
  SkeletonItem,
  Text,
  makeStyles,
  tokens,
} from '@fluentui/react-components';
import {
  Edit24Regular,
  Save24Regular,
  Dismiss24Regular,
  Options24Regular,
  ArrowClockwise24Regular,
} from '@fluentui/react-icons';
import { useTranslation } from 'react-i18next';
import AppContext from '../AppContext';
import {
  buildPanelCatalog,
  type PanelDefinition,
} from './customizable/panelCatalog';
import { packLayout, placePanel } from './customizable/dashboardLayout';
import {
  DashboardComponentsProvider,
  useDashboardComponents,
} from './customizable/DashboardComponentsProvider';
import {
  loadDashboardState,
  saveDashboardState,
} from './customizable/dashboardStorage';
import CustomizeDrawer from './customizable/CustomizeDrawer';

const ROW_HEIGHT = 30;
const COLS = 12;
const MARGIN: [number, number] = [12, 12];

// Free-form placement on the grid: `type: null` means panels never "float up"
// to fill empty space, so a panel can be dragged straight down and stay in the
// same column (with gaps allowed). `preventCollision` stops a drag from landing
// on top of another panel (it blocks instead of overlapping).
const FREEFORM_COMPACTOR = { ...noCompactor, preventCollision: true };

const useStyles = makeStyles({
  root: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalM,
    padding: tokens.spacingVerticalM,
  },
  toolbar: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    rowGap: tokens.spacingVerticalS,
    columnGap: tokens.spacingHorizontalS,
  },
  actions: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    marginLeft: 'auto',
  },
  panel: {
    height: '100%',
    overflow: 'hidden',
    padding: 0,
    backgroundColor: tokens.colorNeutralBackground2,
    boxShadow: tokens.shadow4,
  },
  panelEditing: {
    height: '100%',
    overflow: 'hidden',
    padding: 0,
    cursor: 'move',
    backgroundColor: tokens.colorNeutralBackground2,
    // In edit mode: drop the shadow and show a dashed border instead.
    boxShadow: 'none',
    border: `1px dashed ${tokens.colorBrandStroke1}`,
  },
  empty: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: tokens.spacingVerticalS,
    padding: '64px 16px',
    color: tokens.colorNeutralForeground3,
  },
  // Skeleton grid shown while the component catalog is still loading, so the
  // page never flashes the "no panels" empty state during the initial fetch.
  skeletonGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
    gap: tokens.spacingHorizontalM,
  },
  skeletonCard: {
    height: '160px',
    borderRadius: tokens.borderRadiusMedium,
    backgroundColor: tokens.colorNeutralBackground2,
    boxShadow: tokens.shadow4,
  },
  // Establish a stable positioning context directly around the grid so each
  // grid item's offsetParent resolves to this wrapper (and not a scrolling
  // ancestor). RGL computes the drag-start position from offsetParent geometry
  // + scrollTop; if the offsetParent is a scroll container, the card jumps away
  // from the pointer on grab.
  gridWrapper: {
    position: 'relative',
  },
  // The library's grid overlay, shown only in edit mode behind the panels.
  gridBackground: {
    position: 'absolute',
    inset: 0,
    pointerEvents: 'none',
    zIndex: 0,
  },
});

const DashboardInner = () => {
  const styles = useStyles();
  const { t } = useTranslation();
  const context = useContext(AppContext);
  const { width, containerRef, mounted } = useContainerWidth();

  // Set the header tab once on mount. `context` is recreated on every Home
  // render, so depending on it here would re-run this effect in a loop
  // (setSelectedTab -> Home re-renders -> new context -> effect again).
  const setSelectedTab = context.setSelectedTab;
  useEffect(() => {
    setSelectedTab('Dashboard');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Available components come from the backend (merged across /a, /i, /p),
  // shared via the provider so the catalog is fetched only once.
  const {
    components,
    loading: componentsLoading,
    failed: componentsFailed,
    reload: reloadComponents,
  } = useDashboardComponents();

  const panels = useMemo(() => buildPanelCatalog(components), [components]);
  const panelMap = useMemo(
    () => new Map(panels.map((p) => [p.key, p])),
    [panels]
  );

  // `null` user state means "the user has not chosen yet" (no saved layout and
  // no toggles this session). In that case we render a derived default instead
  // of writing it back through an effect: first-time users see every available
  // panel, packed onto the grid, as soon as the catalog loads. The first
  // interaction (toggle / save / cancel) sets concrete state and pins it.
  const persisted = useMemo(() => loadDashboardState(), []);
  const [userState, setUserState] = useState<{
    enabledPanels: string[];
    layout: LayoutItem[];
  } | null>(persisted);
  const [editMode, setEditMode] = useState(false);
  const [customizeOpen, setCustomizeOpen] = useState(false);
  // Bumped by the global Refresh CTA. Used as the grid's React `key` so every
  // panel remounts and re-runs its options/data fetches from scratch.
  const [refreshKey, setRefreshKey] = useState(0);

  const enabledPanels = userState?.enabledPanels ?? panels.map((p) => p.key);
  const layout = userState?.layout ?? packLayout(panels, COLS);

  // "Reload the page from scratch": re-fetch the component catalog and remount
  // all panels so each re-fetches its own options and data.
  const handleRefreshAll = () => {
    reloadComponents();
    setRefreshKey((k) => k + 1);
  };

  const handleSave = () => {
    setEditMode(false);
    saveDashboardState({ enabledPanels, layout });
  };

  const handleCancel = () => {
    setEditMode(false);
    setUserState(loadDashboardState());
  };

  const handleTogglePanel = (key: string, enabled: boolean) => {
    const panel = panelMap.get(key);
    if (enabled && (!panel || enabledPanels.includes(key))) return;

    const nextEnabled = enabled
      ? [...enabledPanels, key]
      : enabledPanels.filter((k) => k !== key);

    // Adding a panel places it via the same shelf logic as the default packing,
    // so toggled-on panels land where packing would put them (never overlapping).
    const nextLayout = enabled
      ? [...layout, placePanel(layout, panel!, COLS)]
      : layout.filter((l) => l.i !== key);

    setUserState({ enabledPanels: nextEnabled, layout: nextLayout });

    // Outside edit mode, persist toggles immediately. In edit mode the explicit
    // Save commits the change together with any layout edits.
    if (!editMode) {
      saveDashboardState({ enabledPanels: nextEnabled, layout: nextLayout });
    }
  };

  const visiblePanels = useMemo(
    () =>
      enabledPanels
        .map((key) => panelMap.get(key))
        .filter((p): p is PanelDefinition => p != null),
    [enabledPanels, panelMap]
  );

  return (
    <div className={styles.root}>
      <div className={styles.toolbar}>
        <Button
          appearance="outline"
          icon={<ArrowClockwise24Regular />}
          disabled={componentsLoading}
          onClick={handleRefreshAll}
        >
          {t('refresh', 'Refresh')}
        </Button>
        <div className={styles.actions}>
          <Button
            appearance="subtle"
            icon={<Options24Regular />}
            disabled={componentsLoading}
            onClick={() => setCustomizeOpen(true)}
          >
            {t('customize', 'Customize')}
          </Button>
          {editMode ? (
            <>
              <Button
                appearance="primary"
                icon={<Save24Regular />}
                onClick={handleSave}
              >
                {t('save', 'Save')}
              </Button>
              <Button icon={<Dismiss24Regular />} onClick={handleCancel}>
                {t('cancel', 'Cancel')}
              </Button>
            </>
          ) : (
            <Button
              appearance="primary"
              icon={<Edit24Regular />}
              disabled={componentsLoading}
              onClick={() => setEditMode(true)}
            >
              {t('editLayout', 'Edit layout')}
            </Button>
          )}
        </div>
      </div>

      {componentsLoading && panels.length === 0 ? (
        // INITIAL catalog fetch only (no panels yet): show placeholder cards
        // instead of the "no panels" empty state. A background refetch (the
        // global Refresh) keeps the existing grid mounted so panels refresh in
        // place rather than unmounting/remounting (which aborted their fetches).
        <Skeleton>
          <div className={styles.skeletonGrid}>
            {Array.from({ length: 6 }, (_, i) => (
              <SkeletonItem key={i} className={styles.skeletonCard} />
            ))}
          </div>
        </Skeleton>
      ) : visiblePanels.length === 0 ? (
        <div className={styles.empty}>
          <Text>
            {t('noPanels', 'No panels selected. Use Customize to add some.')}
          </Text>
          <Button appearance="primary" onClick={() => setCustomizeOpen(true)}>
            {t('customize', 'Customize')}
          </Button>
        </div>
      ) : (
        <div ref={containerRef} className={styles.gridWrapper}>
          {mounted && editMode && (
            // Library-provided grid overlay, shown only while editing. Draw a
            // couple of extra rows below the content so there is visible empty
            // grid to drop panels into.
            <GridBackground
              className={styles.gridBackground}
              width={width}
              cols={COLS}
              rowHeight={ROW_HEIGHT}
              margin={MARGIN}
              containerPadding={[0, 0]}
              rows={layout.reduce((max, l) => Math.max(max, l.y + l.h), 0) + 2}
            />
          )}
          {mounted && (
            // The editable, persisted 12-column grid. It is fluid, so panels
            // scale with the grid as the window grows.
            <GridLayout
              className="layout"
              style={{ position: 'relative', zIndex: 1 }}
              width={width}
              layout={layout}
              gridConfig={{
                cols: COLS,
                rowHeight: ROW_HEIGHT,
                margin: MARGIN,
                containerPadding: [0, 0],
              }}
              compactor={FREEFORM_COMPACTOR}
              dragConfig={{ enabled: editMode }}
              resizeConfig={{ enabled: editMode }}
              onLayoutChange={(current) => {
                if (editMode) {
                  setUserState({ enabledPanels, layout: [...current] });
                }
              }}
            >
              {visiblePanels.map((panel) => (
                <div key={panel.key}>
                  <Card
                    appearance="outline"
                    className={editMode ? styles.panelEditing : styles.panel}
                  >
                    {/* `refreshKey` is forwarded into the panel so the global
                        Refresh re-runs its fetches in place (no remount, no
                        aborted request wave). */}
                    {panel.render(refreshKey)}
                  </Card>
                </div>
              ))}
            </GridLayout>
          )}
        </div>
      )}

      <CustomizeDrawer
        open={customizeOpen}
        onClose={() => setCustomizeOpen(false)}
        panels={panels}
        enabledPanels={enabledPanels}
        onToggle={handleTogglePanel}
        loading={componentsLoading}
        failed={componentsFailed}
        onReload={reloadComponents}
      />
    </div>
  );
};

const Dashboard = () => (
  <DashboardComponentsProvider>
    <DashboardInner />
  </DashboardComponentsProvider>
);

export default Dashboard;
